// ---------------------------------------------------------------------------
// readiness.ts — Calculates onboarding readiness score for a WizardState.
// Used in the post-creation dashboard and the drafts grid column.
// ---------------------------------------------------------------------------

import type { WizardState } from './types';

export interface ReadinessCheck {
  id: string;
  label: string;
  passed: boolean;
  step: number;
}

export interface ReadinessResult {
  score: number;          // 0–100
  passed: number;
  total: number;
  checks: ReadinessCheck[];
  tier: 'critical' | 'partial' | 'ready';
}

// ---------------------------------------------------------------------------
// Individual checks
// ---------------------------------------------------------------------------

function check(id: string, label: string, step: number, passed: boolean): ReadinessCheck {
  return { id, label, step, passed };
}

export function calculateReadiness(state: WizardState): ReadinessResult {
  const info = state.departmentInfo ?? {};
  const addr = info.address ?? {};
  const jur = info.jurisdiction ?? {};
  const b = state.billing ?? {};
  const p = state.inspectionPlan ?? {};
  const auth = state.authenticationSettings ?? {};
  const admin = state.initialAdmin ?? {};
  const ids = state.initialDataSetup ?? { inspectionAccounts: [] };

  const checks: ReadinessCheck[] = [
    // Step 1 — Department Info
    check('dept-name', 'Department name set', 1, !!info.name && info.name.length >= 2),
    check('dept-email', 'Contact email set', 1, !!info.contactEmail && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.contactEmail)),
    check('dept-address', 'Street address set', 1, !!addr.street),
    check('dept-zip', 'ZIP code set', 1, !!addr.zip),
    check('dept-timezone', 'Timezone set', 1, !!info.timezone),
    check('dept-type', 'Department type set', 1, !!info.departmentType),
    check('dept-classification', 'Classification set', 1, !!info.departmentClassification),
    check('dept-jurisdiction', 'Jurisdiction (state + county) set', 1, !!jur.state?.code && !!jur.county?.code),

    // Step 2 — Org & Tier
    check('tier', 'Tier selected', 2, !!state.tier),

    // Step 3 — Billing
    check('billing-entity', 'Billing entity set', 3, !!b.billingEntity),
    check('billing-type', 'Subscription type set', 3, !!b.subscriptionType),
    check('billing-start', 'Start date set', 3, !!b.startDate),
    check('billing-contact', 'Billing contact name set', 3, !!b.billingContact?.name),
    check('billing-contact-email', 'Billing contact email set', 3, !!b.billingContact?.email),

    // Step 4 — Inspection Plan
    check('plan-band', 'Volume band set', 4, !!p.volumeBand),
    check('plan-max', 'Max series/year > 0', 4, !!p.maxSeriesPerYear && p.maxSeriesPerYear > 0),
    check('plan-term', 'Commitment term set', 4, !!p.commitmentTerm),
    check('plan-overage', 'Overage policy set', 4, !!p.overagePolicy),
    check('plan-source', 'Plan source set', 4, p.source === 'department' || p.source === 'organization'),

    // Step 5 — Contacts
    check('contacts-present', 'At least one contact added', 5, state.contacts.length > 0),
    check('contacts-primary', 'Primary contact designated', 5, state.contacts.some((c) => c.isPrimary)),

    // Step 7 — Auth
    check('auth-method', 'Auth method set', 7, !!auth.authMethod),
    check('admin-first', 'Admin first name set', 7, !!admin.firstName),
    check('admin-last', 'Admin last name set', 7, !!admin.lastName),
    check('admin-email', 'Admin email set', 7, !!admin.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(admin.email)),

    // Step 8 — Initial Data Setup
    check('accounts-present', 'At least one inspection account', 8, ids.inspectionAccounts.length > 0),
    check('accounts-named', 'All inspection accounts named', 8, ids.inspectionAccounts.length > 0 && ids.inspectionAccounts.every((a) => !!a.name)),
    check('accounts-default', 'Default inspection account set', 8, ids.inspectionAccounts.some((a) => a.isDefault)),

    // Step 9 — TOS
    check('tos', 'Terms of Service acknowledged', 9, !!state.tosAcknowledged),
  ];

  const passed = checks.filter((c) => c.passed).length;
  const total = checks.length;
  const score = Math.round((passed / total) * 100);

  const tier: ReadinessResult['tier'] =
    score === 100 ? 'ready' : score >= 60 ? 'partial' : 'critical';

  return { score, passed, total, checks, tier };
}
